import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ChildActivationEnd, NavigationEnd, Router } from '@angular/router';
import { AuthService } from '@common/services';
import { CasheaAnnouncementService } from '@common/services/cashea-announcement.service';
import { UserService } from '@modules/auth/services/user.service';
import { LineMilestoneWatcherService } from '@modules/creditos/services/line-milestone-watcher.service';
import { OnboardingCelebrationModalComponent } from '@modules/onboarding/components';
import { OnboardingAnalyticsService, OnboardingService } from '@modules/onboarding/services';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, takeUntil } from 'rxjs/operators';

import { VersionCheckService } from './services/version-check.service';

@Component({
    selector: 'app-root',
    template: `
        <div *ngIf="updateAvailable" class="update-banner">
            Una nueva versión esta disponible, refresca la página para que Fina funcione correctamente.
            <button class="refresh-button" (click)="reloadApp()">Refrescar</button>
        </div>
        <sbpro-toast-view class="ngb-toasts" aria-live="polite" aria-atomic="true"></sbpro-toast-view>
        <router-outlet></router-outlet>
        <fina-onboarding-blackout></fina-onboarding-blackout>
        <!-- Modal de celebración global: se dispara en cualquier ruta cuando una fase llega a 100%
             (vía OnboardingService.celebration$) o al cerrar el onboarding (completion$, una sola vez). -->
        <fina-onboarding-celebration-modal #celebrationModal (closed)="onCelebrationClosed()"></fina-onboarding-celebration-modal>
        <!-- FINA-3378: «Pagaste tu préstamo» / «Subiste a Nivel N» de Fina Presta, en cualquier ruta y una
             sola vez por crédito (lo decide LineMilestoneWatcherService). -->
        <fina-line-done-modal
            *ngIf="lineMilestone.modal$ | async as milestone"
            [line]="milestone.line"
            [credito]="milestone.credito"
            (closed)="lineMilestone.close()"
            (acknowledged)="lineMilestone.close()"
            (requestNext)="onLineMilestoneRequestNext()"
        ></fina-line-done-modal>
    `,
    styles: [
        `
            .update-banner {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                background: #ff9800;
                color: white;
                text-align: center;
                padding: 10px;
                z-index: 9999;
            }

            .refresh-button {
                background: white;
                color: #ff9800;
                border: none;
                padding: 4px 12px;
                margin-left: 8px;
                border-radius: 12px;
                cursor: pointer;
            }

            .refresh-button:hover {
                background: #f5f5f5;
            }
        `,
    ],
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
    updateAvailable = false;

    @ViewChild('celebrationModal') celebrationModal?: OnboardingCelebrationModalComponent;

    private readonly destroy$ = new Subject<void>();

    constructor(
        public router: Router,
        private titleService: Title,
        private authService: AuthService,
        private onboarding: OnboardingService,
        private onboardingAnalytics: OnboardingAnalyticsService,
        private versionCheckService: VersionCheckService,
        public lineMilestone: LineMilestoneWatcherService,
        private userService: UserService,
        private casheaAnnouncement: CasheaAnnouncementService,
    ) {
        this.router.events
            .pipe(
                takeUntil(this.destroy$),
                filter(event => event instanceof ChildActivationEnd),
            )
            .subscribe(event => {
                let snapshot = (event as ChildActivationEnd).snapshot;

                while (snapshot.firstChild !== null) {
                    snapshot = snapshot.firstChild;
                }
                this.titleService.setTitle(snapshot.data.title || 'FINA Business');
            });
    }

    ngOnInit(): void {
        if (this.authService.tryRedirectUnifiedLoginHostToLastTenant()) {
            return;
        }
        this.authService.clearStaleSessionOnUnifiedLoginHost();
        this.authService.autoLogin();
        document.body.classList.add('notranslate');
        this.versionCheckService.initVersionCheck('/version.json').subscribe(() => {
            // Service is filtered to only emit when an update is available; once
            // we know the running bundle is stale, we keep the banner up until
            // the user clicks "Refrescar" (which calls `reloadApp` below).
            this.updateAvailable = true;
        });
        this.watchLineMilestones();
        this.casheaAnnouncement.start();
    }

    /**
     * FINA-3378: el vigía de Fina Presta revisa al iniciar sesión el dueño (`force`, una vez por
     * tenant/dueño: un refresh del token no reemite), al cambiar de ruta y al volver a la pestaña (con
     * su tope de 5 minutos).
     */
    private watchLineMilestones(): void {
        this.userService.user$
            .pipe(
                map(user => (user?.isOwner && user?.creditAccess && user?.tenantId ? `${user.tenantId}` : null)),
                distinctUntilChanged(),
                filter((tenantId): tenantId is string => !!tenantId),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.lineMilestone.check({ force: true }));
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this.destroy$),
            )
            .subscribe(() => this.lineMilestone.check());
        document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    private readonly onVisibilityChange = (): void => {
        if (document.visibilityState === 'visible') this.lineMilestone.check();
    };

    /** «Pedir mi próximo préstamo»: cierra y lleva a Fina Presta. */
    onLineMilestoneRequestNext(): void {
        this.lineMilestone.close();
        this.router.navigate(['/creditos/general']);
    }

    ngAfterViewInit(): void {
        // Modal de celebración global: OnboardingService.celebration$ emite cuando una fase cruza a 100%
        // (tras el update por socket/HTTP). Se hospeda en el shell para que aparezca en cualquier ruta.
        // Se suscribe en ngAfterViewInit (no ngOnInit) para garantizar que el @ViewChild `celebrationModal`
        // ya esté resuelto cuando `celebration$` emita.
        this.onboarding.celebration$.pipe(takeUntil(this.destroy$)).subscribe(({ config, theme, tasksDone, tasksTotal }) => {
            // Con la celebración abierta, el vigía de Fina Presta no se le encima.
            this.lineMilestone.hold('celebration');
            this.celebrationModal?.open(config, theme, tasksDone, tasksTotal);
        });
        // Modal final del onboarding (FINA-3377): mismo componente, solo cuando el claim atómico lo confirma.
        // Igual que la celebración de fase, bloquea al vigía de Fina Presta hasta que se cierre.
        this.onboarding.completion$.pipe(takeUntil(this.destroy$)).subscribe(({ config, theme, tasksDone, tasksTotal }) => {
            this.lineMilestone.hold('celebration');
            this.celebrationModal?.open(config, theme, tasksDone, tasksTotal);
        });
    }

    ngOnDestroy(): void {
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** El modal de celebración se cerró → el servicio de analytics decide si dispara `onboarding_completed`
     *  (solo si era el de la última fase; cierre del embudo). El shell solo reenvía la señal. */
    onCelebrationClosed(): void {
        this.onboardingAnalytics.celebrationClosed();
        this.lineMilestone.release('celebration');
        // FINA-3431: el anillo y el banner del onboarding se retiran al cerrar el modal final, no antes.
        this.onboarding.releaseCompletionHold();
    }

    reloadApp() {
        // Clear cache and reload
        window.location.reload();
    }
}
